import { type FC, useState } from 'react'
import * as Sentry from '@sentry/react'
import { type AccountOpeningDescriptor, type AccountOpeningHandoffStep } from '../../../models/AccountOpeningDescriptor'
import {
  isTerminalStatus,
  statusHeadline,
  statusLabelsOf,
  statusTone,
  storedOutcome,
  storedStatusCheckedAt,
  storedStatusCode,
  storedStatusDetail,
  type SubmissionOutcome,
} from '../domain/connectorResult'
import { handoffCopy, trackingCopy } from '../descriptor/stepCopy'
import { useRefreshAccountOpeningStatusMutation } from '../../../store/accountOpening/accountOpening'
import { asOfText } from '../domain/asOf'
import { useAccountTimezone } from '../../../common/useAccountTimezone'
import { HintText, IconActionButton, StatusChip, WarningText } from './flowPrimitives'

interface HandoffStepProps {
  step: AccountOpeningHandoffStep
  result: SubmissionOutcome | null
  descriptor: AccountOpeningDescriptor | undefined
  accountId: string
  openingId: string | null
  savedMetadata: Record<string, unknown>
}

export const HandoffStep: FC<HandoffStepProps> = ({ step, result, descriptor, accountId, openingId, savedMetadata }) => {
  const copy = handoffCopy(step)
  const tracking = trackingCopy(descriptor?.tracking)
  const accountTimeZone = useAccountTimezone()
  const [refreshAccountOpeningStatus, { isLoading }] = useRefreshAccountOpeningStatusMutation()
  const [refreshed, setRefreshed] = useState<Record<string, unknown> | null>(null)
  const [failed, setFailed] = useState(false)

  const metadata = refreshed ?? savedMetadata
  const statusCode = storedStatusCode(metadata) ?? result?.statusCode ?? null
  const statusDetail = storedStatusDetail(metadata)
  const checkedAt = asOfText(tracking.checkedAtLabel, storedStatusCheckedAt(metadata) ?? undefined, accountTimeZone)

  const url = storedOutcome(metadata).handoffUrl ?? result?.handoffUrl ?? null
  const canRefresh = !!openingId && !isTerminalStatus(descriptor, statusCode)

  const handleRefresh = async () => {
    if (!openingId) {
      return
    }
    setFailed(false)
    try {
      const response = await refreshAccountOpeningStatus({ accountId, openingId }).unwrap()
      setRefreshed(response.data.metadata ?? {})
    } catch (error) {
      setFailed(true)
      Sentry.captureException(error)
    }
  }

  return (
    <div className='d-flex flex-column zocks-gap-1'>
      {statusCode ? (
        <div className='d-flex align-items-center zocks-gap-1 flex-wrap'>
          <StatusChip tone={statusTone(descriptor, statusCode)} size='medium'>
            {statusHeadline(statusLabelsOf(descriptor), statusCode, statusDetail)}
          </StatusChip>
          {canRefresh ? (
            <IconActionButton
              icon='bi-arrow-repeat'
              label={tracking.refreshLabel}
              loadingLabel={tracking.checkingLabel}
              loading={isLoading}
              onClick={handleRefresh}
            />
          ) : null}
        </div>
      ) : null}

      {failed ? <WarningText>{tracking.checkFailedLabel}</WarningText> : checkedAt ? <HintText>{checkedAt}</HintText> : null}

      {url ? (
        <a className='paragraph-small align-self-start' href={url} target='_blank' rel='noopener noreferrer'>
          {copy.linkLabel}
        </a>
      ) : (
        <HintText>{copy.missing}</HintText>
      )}
    </div>
  )
}
